import React from 'react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Refund <span className="text-primary">Policy</span>
            </h1>
            {/* <p className="text-xl text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p> */}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-soft-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Overview</h2>
              <p className="text-gray-600 mb-6">
                At Checkstall, we strive to provide excellent service and quality products. However, 
                we understand that sometimes refunds may be necessary. This policy outlines the terms 
                and conditions for refunds on our platform.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Refund Eligibility</h2>
              <p className="text-gray-600 mb-4">
                Refunds may be available under the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Product defects or quality issues not meeting specifications</li>
                <li>Wrong items received due to our error</li>
                <li>Significant delays in delivery beyond our control</li>
                <li>Products damaged during shipping (with proper documentation)</li>
                <li>Non-delivery of products within the specified timeframe</li>
                <li>Duplicate payments or billing errors</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Refund Timeframes</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3.1 Order Refunds</h3>
              <p className="text-gray-600 mb-4">
                For order-related refunds, you must request a refund within:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Before Shipment:</strong> 24 hours from order placement</li>
                <li><strong>After Shipment:</strong> 7 days from delivery confirmation</li>
                <li><strong>Quality Issues:</strong> 14 days from delivery confirmation</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">3.2 Service Refunds</h3>
              <p className="text-gray-600 mb-6">
                For service-related refunds (subscriptions, premium features), you must request a refund 
                within 30 days of the service activation or renewal.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Refund Process</h2>
              <p className="text-gray-600 mb-4">
                To request a refund, please follow these steps:
              </p>
              <ol className="list-decimal pl-6 text-gray-600 mb-6">
                <li>Log into your Checkstall account</li>
                <li>Navigate to the order or transaction you want to refund</li>
                <li>Click on the "Request Refund" button</li>
                <li>Provide a detailed reason for the refund request</li>
                <li>Upload any supporting documentation (photos, videos, etc.)</li>
                <li>Submit your request and wait for our review</li>
              </ol>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Refund Processing</h2>
              <p className="text-gray-600 mb-4">
                Once your refund request is approved:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Refunds will be processed within 5-10 business days</li>
                <li>You will receive a confirmation email with refund details</li>
                <li>Refunds will be credited to your original payment method</li>
                <li>For wallet payments, refunds will be credited to your Checkstall wallet</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Non-Refundable Items</h2>
              <p className="text-gray-600 mb-4">
                The following items and situations are generally not eligible for refunds:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Custom-made or personalized products</li>
                <li>Digital products or services already delivered</li>
                <li>Products damaged due to customer misuse or negligence</li>
                <li>Refund requests made after the specified timeframes</li>
                <li>Products returned without proper documentation</li>
                <li>Shipping costs (unless the return is due to our error)</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Partial Refunds</h2>
              <p className="text-gray-600 mb-6">
                In some cases, we may offer partial refunds instead of full refunds. This may occur when:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Only part of your order is affected by an issue</li>
                <li>You've used or partially consumed the product</li>
                <li>Minor defects that don't significantly impact product functionality</li>
                <li>You request a refund for reasons not covered under our full refund policy</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Return Shipping</h2>
              <p className="text-gray-600 mb-4">
                For physical products that need to be returned:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>We will provide a prepaid return shipping label for defects or our errors</li>
                <li>For other returns, you may be responsible for return shipping costs</li>
                <li>Products must be returned in original packaging when possible</li>
                <li>We reserve the right to refuse returns that don't meet our requirements</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Dispute Resolution</h2>
              <p className="text-gray-600 mb-6">
                If you disagree with our refund decision, you can:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Contact our customer support team for a second review</li>
                <li>Provide additional documentation to support your case</li>
                <li>Request to speak with a supervisor or manager</li>
                <li>Escalate the matter through our formal dispute resolution process</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Refund Methods</h2>
              <p className="text-gray-600 mb-4">
                Refunds will be processed using the same payment method used for the original transaction:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Credit/Debit Cards:</strong> Refunded to the original card (3-5 business days)</li>
                <li><strong>Bank Transfer:</strong> Refunded to the original account (5-10 business days)</li>
                <li><strong>Checkstall Wallet:</strong> Credited immediately to your wallet balance</li>
                <li><strong>Cryptocurrency:</strong> Refunded to the original wallet address</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">11. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about our refund policy or need assistance with a refund request:
              </p>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="text-gray-700 mb-2"><strong>Email:</strong> support@checkstall.com</p>
                <p className="text-gray-700 mb-2"><strong>Phone:</strong> +234 800 123 4567</p>
                <p className="text-gray-700 mb-2"><strong>Live Chat:</strong> Available 24/7 on our website</p>
                <p className="text-gray-700"><strong>Address:</strong> 123 Victoria Island, Lagos, Nigeria 101241</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;


